import * as Sentry from "@sentry/react";
import axios from "axios";
import axiosRetry from "axios-retry";

import { nukeAuthHeaderAxiosReqTransform } from "../../components/utils/util";

import { notifyError, notifySuccess } from "./notify";
import {
  PROJECT_UPLOADING_LOCAL_FILES,
  PROJECT_UPLOADING_SET_FILES,
  PROJECT_UPLOADING_SET_PROGRESS_LOCAL_FILES,
  PROJECT_UPLOADING_SET_COMPLETE_LOCAL_FILES,
  PROJECT_UPLOADING_SET_PROGRESS_CLOUD_FILES,
  PROJECT_UPLOADING_DELETE_FILES,
  PROJECT_UPLOADING_BULK_UPDATE_FILES,
  PROJECT_SET_NEED_TO_DELETE_FLAG,
  GET_UPDATE_FILES,
  ADD_FILE,
} from "./types";

export const setLocalFilesToUpload = (filesToUpload, projectId, foldersId) => async (dispatch) => {
  dispatch({
    type: PROJECT_UPLOADING_SET_FILES,
    payload: { filesToUpload, projectId, total: filesToUpload.length, folders: foldersId },
  });
  dispatch(uploadLocalFiles());
};

export const uploadLocalFiles = () => async (dispatch, getState) => {
  const { stories: storiesToGetPresignedUrls } = getState().uploadFiles;

  storiesToGetPresignedUrls.sort((a, b) => a._id.localeCompare(b._id));
  // first go through all the files and start the upload process
  for (const story of storiesToGetPresignedUrls) {
    await dispatch(uploadFileParts(story));
  }

  setTimeout(() => {
    // After upload, get final stories status to show the toast
    const { stories: storiesAfterUpload } = getState().uploadFiles;
    const completedStories = storiesAfterUpload.filter((story) => story.complete);
    dispatch(notifySuccess(`${completedStories.length} files have been uploaded!`));
  }, 10000); // We wait 10 seconds to make sure last file is uploaded
};

export const uploadFileParts = (storyPresigned) => async (dispatch, getState) => {
  const { stories, projectId, folders } = getState().uploadFiles;
  const existStoryInStore = stories.find((story) => story.storyId === storyPresigned.storyId);
  const userFlags = (getState() as RootState).featureFlags?.flags;

  // we have to check if the file still exist because we could have deleted one that has not started uploading yet.
  if (existStoryInStore) {
    const FILECHUNKSIZE = 50 * 1024 * 1024;

    const res = await axios.get(
      `/stories/${storyPresigned.storyId}/upload-config` +
        `?type=${storyPresigned.mediaType}&numUploadParts=${Math.ceil(storyPresigned.file.size / FILECHUNKSIZE)}`
    );
    const multipartUploadConfig = res.data;
    const uploadPartURLs = multipartUploadConfig.Parts as string[];
    const axiosForUpload = axios.create();
    const source = axios.CancelToken.source();
    delete axiosForUpload.defaults.headers.put["Content-Type"];
    axiosRetry(axiosForUpload, { retries: 1 });

    // we need to set a flag if need to cancel the whole process if story is deleted
    let shouldCancelUpload = false;

    try {
      await uploadPartURLs.reduce(async (agg, partURL, partIndex) => {
        await agg;

        // we cancel reduce if story deleted
        if (shouldCancelUpload || !storyPresigned) {
          throw new Error("Upload process was cancelled");
        }

        const start = partIndex * FILECHUNKSIZE;
        const end = (partIndex + 1) * FILECHUNKSIZE;
        const blob =
          end < storyPresigned.file.size ? storyPresigned.file.slice(start, end) : storyPresigned.file.slice(start);

        return axiosForUpload
          .put(partURL, blob, {
            transformRequest: nukeAuthHeaderAxiosReqTransform,
            cancelToken: source.token,
            onUploadProgress: (uploadProgressEvent) => {
              dispatch(
                uploadProgressIndicator(
                  storyPresigned._id,
                  parseInt(uploadProgressEvent.loaded, 10) + partIndex * FILECHUNKSIZE,
                  storyPresigned.file.size,
                  source
                )
              );
            },
          })
          .catch(function (thrown) {
            // if story is deleted we set flag shouldCancelUpload to true to cancel reduce behavior
            if (axios.isCancel(thrown)) {
              shouldCancelUpload = true;
            }
          });
      }, Promise.resolve());

      await axios.post(`/stories/${storyPresigned.storyId}/media/upload/completion`, {
        key: multipartUploadConfig.Key,
        uploadId: multipartUploadConfig.UploadId,
      });

      await axios.post(`/stories/${storyPresigned.storyId}/conclude`, {
        // participants: [],
        // tags: [],
        // title: story.file.name,
        notes: "",
        story_id: storyPresigned.storyId,
        groups: [],
        projects: [],
        project: projectId,
        folders: folders,
        emails: [],
        notify_group: false,
        termsAccepted: false,
        is_upload: true,
        mediaKey: res.data.Key,
        mediaType: storyPresigned.mediaType,
        export_to_permanent_org: false,
        file_name: storyPresigned.file.name.split(".").slice(0, -1).join("."),
      });

      const isFeatureFlagAutomacitTranscode: any = userFlags?.automaticTranscode
        ? userFlags?.automaticTranscode
        : false;
      console.log("Determine automatic transcription: ", isFeatureFlagAutomacitTranscode);

      if (isFeatureFlagAutomacitTranscode) {
        await axios.post(`/stories/${storyPresigned.storyId}/transcode`);
      } else {
        await axios.post(`/stories/${storyPresigned.storyId}/deepgram_transcribe`);
      }

      await dispatch(completeLocalUpload(storyPresigned.storyId));
    } catch (error) {
      // finally we remove the story from redux to update the view
      if (shouldCancelUpload) {
        dispatch(removeStoryFromStore(storyPresigned.storyId));
      } else {
        dispatch(failedLocalUpload(storyPresigned.storyId));
      }
    }
  }
};

export const uploadProgressIndicator = (storyId, uploadedSoFar, fileSize, source) => async (dispatch, getState) => {
  const { stories, filesCompleted } = getState().uploadFiles;
  const storyToUpdateProgress = stories.find((story) => story.storyId === storyId);

  // before we update the progress we need to check if the files need yo be removed
  if (storyToUpdateProgress.needToDelete) {
    source.cancel();
  }

  const uploadProgress = `${Math.max(1, Math.round((uploadedSoFar / fileSize) * 100))}%`;
  const uploadedPartCount = (storyToUpdateProgress.uploadedPartCount as number) + 1;
  const updatedStory = { ...storyToUpdateProgress, uploadProgress, uploadedPartCount };
  const restOfStories = stories.filter((story) => story.storyId !== storyId);
  const updatedStories = [...restOfStories, updatedStory];
  updatedStories.sort((a, b) => a._id.localeCompare(b._id));
  const uploadedFraction = Math.max(1, (uploadedSoFar / fileSize) * 100) / 100;

  dispatch({
    type: PROJECT_UPLOADING_SET_PROGRESS_LOCAL_FILES,
    payload: {
      updatedStories,
      current: uploadedFraction === 1 ? 0 : uploadedFraction,
      filesCompleted: uploadedFraction === 1 ? filesCompleted + 1 : filesCompleted,
    },
  });
};

export const completeLocalUpload = (storyId) => async (dispatch, getState) => {
  // we need to wait until the image is generated
  setTimeout(async () => {
    const { stories } = getState().uploadFiles;
    const res = await axios.get(`stories/${storyId}`);
    if (res.data.thumbnailURL) {
      const storyToUpdateProgress = stories.find((story) => story._id === storyId);
      const uploadProgress = "100%";
      const complete = true;
      const updatedStory = {
        ...storyToUpdateProgress,
        _id: res.data._id,
        uploadProgress,
        complete,
        thumbnailURL: res.data.thumbnailURL,
      };
      const restOfStories = stories.filter((story) => story._id !== storyId);
      const updatedStories = [...restOfStories, updatedStory];
      const areLocalStoriesCompleted = updatedStories.every((story) => story.complete);
      updatedStories.sort((a, b) => a._id.localeCompare(b._id));
      dispatch({
        type: PROJECT_UPLOADING_SET_COMPLETE_LOCAL_FILES,
        payload: {
          updatedStories,
          uploadingFiles: !areLocalStoriesCompleted,
        },
      });
    }
  }, 8000);
};

export const failedLocalUpload = (storyId) => async (dispatch, getState) => {
  const { stories } = getState().uploadFiles;
  const storyToUpdateProgress = stories.find((story) => story.storyId === storyId);
  const uploadProgress = "FAILED";
  const complete = false;
  const updatedStory = { ...storyToUpdateProgress, uploadProgress, complete };
  const restOfStories = stories.filter((story) => story.storyId !== storyId);
  const updatedStories = [...restOfStories, updatedStory];
  updatedStories.sort((a, b) => a._id.localeCompare(b._id));

  dispatch({
    type: PROJECT_UPLOADING_SET_PROGRESS_LOCAL_FILES,
    payload: {
      updatedStories,
    },
  });
};

export const addFlagToDeleteLocalFile = (storyId) => async (dispatch, getState) => {
  const { stories } = getState().uploadFiles;
  const fileToDelete = stories.find((story) => story.storyId === storyId);
  const updatedStory = { ...fileToDelete, needToDelete: true };
  const restOfStories = stories.filter((story) => story.storyId !== storyId);
  const updatedStories = [...restOfStories, updatedStory];
  updatedStories.sort((a, b) => a._id.localeCompare(b._id));

  dispatch({
    type: PROJECT_SET_NEED_TO_DELETE_FLAG,
    payload: {
      updatedStories: updatedStories,
    },
  });
};

export const removeStoryFromStore = (storyId) => async (dispatch, getState) => {
  const { stories, total, filesCompleted, current } = getState().uploadFiles;
  const fileToDelete = stories.find((story) => story.storyId === storyId);
  const filteredFiles = stories.filter((story) => story.storyId !== storyId);
  const areStoriesRemaining = filteredFiles.length > 0;

  dispatch({
    type: PROJECT_UPLOADING_DELETE_FILES,
    payload: {
      updatedStories: filteredFiles,
      total: total - 1,
      current: fileToDelete.complete ? current : 0,
      filesCompleted: fileToDelete.complete ? filesCompleted - 1 : filesCompleted,
      uploadingFiles: areStoriesRemaining,
    },
  });
};

export const deleteFileUpload = (storyId) => async (dispatch, getState) => {
  const { stories } = getState().uploadFiles;

  const fileToDelete = stories.find((story) => story.storyId === storyId);
  const isComplete = fileToDelete.complete;
  const isNotStarted = fileToDelete.uploadedPartCount === 0 && !fileToDelete.complete;

  // if the story is already upload we delete and remove it from redux
  if (isComplete) {
    await axios
      .delete(`/stories/${storyId}`)
      .then(() => {
        dispatch(removeStoryFromStore(storyId));
      })
      .catch((err) => {
        dispatch(notifyError("ups! failed to delete story"));
        Sentry.withScope((scope) => {
          scope.setTag("storyId", storyId);
          scope.setTag("method", "deleteFileUpload");
          Sentry.captureException(err);
        });
      });
    return;
  }
  // if story did not begin to upload to the cloud we just remove it from redux
  if (isNotStarted) {
    dispatch(removeStoryFromStore(storyId));
    return;
  }

  // at this point the story already began to upload, so we activate the flag to remove it when request is cancel
  dispatch(addFlagToDeleteLocalFile(storyId));
};

export const checkUploadingStatus = () => async (dispatch) => {
  const uploadingFiles = JSON.parse(localStorage.getItem("uploadingFiles"));

  try {
    const baseUrl = "/stories/status_bulk_upload";

    if (uploadingFiles) {
      const res = await axios.post(baseUrl, {
        stories: uploadingFiles.stories.map((story) => ({ _id: story.storyId })),
      });
      if (res.data.length > 0) {
        const filesCompleted = res.data?.filter((story) => story.available === true);
        const uploading = res.data?.some((story) => story.available === false);
        const newStories = res.data.map((story) => ({ available: story.available, ...story.story }));

        if (!uploading) {
          dispatch({
            type: PROJECT_UPLOADING_SET_PROGRESS_CLOUD_FILES,
            payload: {
              uploadingFiles: false,
              filesCompleted: filesCompleted.length > 0 ? filesCompleted.length : 0,
              updatedStories: newStories,
              total: uploadingFiles.stories.length,
              projectId: uploadingFiles.projectId,
            },
          });

          localStorage.removeItem("uploadingFiles");
          dispatch(notifySuccess("all files upload correctly!"));
        } else {
          dispatch({
            type: PROJECT_UPLOADING_SET_PROGRESS_CLOUD_FILES,
            payload: {
              uploadingFiles: uploading,
              filesCompleted: filesCompleted.length > 0 ? filesCompleted.length : 0,
              total: uploadingFiles.stories.length,
              updatedStories: newStories,
              projectId: uploadingFiles.projectId,
            },
          });
        }
      } else {
        localStorage.removeItem("uploadingFiles");
      }
    }
  } catch (error) {
    Sentry.withScope((scope) => {
      scope.setExtra("uploadingFiles", uploadingFiles);
      scope.setTag("method", "checkUploadingStatus");
      Sentry.captureException(error);
    });
    throw error;
  }
};

export const bulkUpdateStories = (storiesUpdated: any) => async (dispatch, getState) => {
  const { stories } = getState().uploadFiles;

  const baseUrl = "/stories/update_bulk_upload";
  // We combine the stories data from the updateBulk form in the frontend with the ones in redux
  const combinedStories = stories.map((story) => {
    const storyUpdated = storiesUpdated.find((storyToUpdate) => storyToUpdate._id === story._id);
    return storyUpdated ? { ...story, ...storyUpdated } : story;
  });

  await axios
    .put(baseUrl, { stories: combinedStories })
    .then(() => {
      dispatch(notifySuccess("Story updated successfully"));
    })
    .catch((err) => {
      dispatch(notifyError("Failed to update bulk stories"));
      Sentry.withScope((scope) => {
        scope.setExtra("storiesUpdated", storiesUpdated);
        scope.setExtra("combinedStories", combinedStories);
        scope.setTag("method", "bulkUpdateStories");
        Sentry.captureException(err);
      });
    });

  dispatch({
    type: PROJECT_UPLOADING_BULK_UPDATE_FILES,
    payload: { stories: combinedStories },
  });
};

export const bulkCreateCloudStories =
  (stories: any, projectId: string, accessToken: string, folders: any) => async (dispatch) => {
    const baseUrl = "/stories/upload_bulk_box";
    try {
      await axios
        .post(baseUrl, { stories: stories, project: projectId, boxToken: accessToken, folders: folders })
        .then((res) => {
          const storiesToUpload = res.data.map((story) => ({
            _id: story._id,
            storyId: story._id,
            title: story.title,
            recordDate: story.record_date,
            type: "cloud",
          }));
          const storiesForLocalStorage = res.data.map((story) => ({ storyId: story._id, type: "cloud" }));
          localStorage.setItem("uploadingFiles", JSON.stringify({ stories: storiesForLocalStorage, projectId }));

          dispatch({
            type: PROJECT_UPLOADING_SET_FILES,
            payload: { filesToUpload: storiesToUpload, projectId, total: stories.length },
          });
        });
    } catch (err) {
      dispatch(notifyError(`Failed to create bulk stories`));
      Sentry.withScope((scope) => {
        scope.setExtra("stories", stories);
        scope.setTag("projectId", projectId);
        scope.setExtra("folders", folders);
        scope.setExtra("stories", stories);
        scope.setTag("method", "bulkCreateCloudStories");
        Sentry.captureException(err);
      });
    }
  };

export const needReencoding = () => async (dispatch, getState) => {
  dispatch({
    type: PROJECT_UPLOADING_LOCAL_FILES,
    // type: PROJECT_UPLOADING_ENCODING_LOCAL_FILES,
    payload: {
      ...getState().uploadLocalFiles,
      localUploadProgress: {
        ...getState().uploadLocalFiles.localUploadProgress,
        needReEncoding: true,
      },
    },
  });
};

export const setFailedLocalUploadingStatus = () => async (dispatch, getState) => {
  dispatch({
    type: PROJECT_UPLOADING_LOCAL_FILES,
    // type: PROJECT_UPLOADING_ENCODING_LOCAL_FILES,
    payload: {
      ...getState().uploadLocalFiles,
      localUploadProgress: {
        ...getState().uploadLocalFiles.localUploadProgress,
        uploadProgress: "UPLOAD_FAILED",
        uploadedPartCount: -1,
      },
    },
  });
};

export const getFiles = (storyId: string) => async (dispatch) => {
  const url = `/stories/${storyId}/list_files`;
  try {
    await axios
      .get(url, {
        headers: { "Content-Type": "application/json" },
      })
      .then((res) => {
        dispatch({
          type: GET_UPDATE_FILES,
          payload: { files: res.data },
        });
      });
  } catch (err) {
    dispatch(notifyError(`Failed while retrieving files`));
  }
};

export const addFile = (storyId: string, payload: any) => async (dispatch) => {
  const url = `/stories/${storyId}/add_file`;
  try {
    const response = await axios.post(url, payload).then((res) => {
      dispatch({
        type: ADD_FILE,
        payload: { file: res.data },
      });
      dispatch(notifySuccess("File uploaded successfully."));
    });
    return response;
  } catch (error) {
    dispatch(notifyError("An error was encountered while file upload."));
    Sentry.withScope((scope) => {
      scope.setTag("storyId", storyId);
      scope.setTag("method", "handleFileUpload");
      Sentry.captureException(error);
    });
    console.error("Failed to upload file: ", error);
    return error;
  }
};

export const updateFileName = (_id, title) => (dispatch, getState) => {
  const files = getState().uploadFiles.filesTab;

  const fileToUpdate = files.find((file) => file._id === _id);
  const fileUpdated = { ...fileToUpdate, title: title };
  const filteredFiles = files.filter((file) => file._id !== _id);
  const filesUpdated = [...filteredFiles, fileUpdated];

  dispatch({
    type: GET_UPDATE_FILES,
    payload: {
      files: filesUpdated,
    },
  });
};

export const updateFileReleaseForm = (_id, releaseForm, storyId) => async (dispatch, getState) => {
  const files = getState().uploadFiles.filesTab;
  const url = `/stories/${storyId}/edit_file/${_id}`;
  const payload = {
    release_form: !releaseForm ?? false,
  };

  const filteredFiles = files.filter((file) => file._id !== _id);

  const fileToUpdate = files.find((file) => file._id === _id);
  const fileUpdated = { ...fileToUpdate, release_form: !releaseForm ?? false };
  const filesUpdated = [...filteredFiles, fileUpdated];

  try {
    const response = await axios.put(url, payload, {
      headers: { "Content-Type": "application/json" },
      params: { release_form: !releaseForm },
    });

    if (response.status === 200) {
      dispatch({
        type: GET_UPDATE_FILES,
        payload: {
          files: filesUpdated,
        },
      });
    }
    dispatch(notifySuccess("File updated successfully."));
  } catch (error) {
    dispatch(notifyError("An error was encountered while updating the file."));
    Sentry.withScope((scope) => {
      scope.setTag("storyId", storyId);
      scope.setExtra("payload", payload);
      scope.setTag("method", "setAsReleaseForm");
      Sentry.captureException(error);
    });
    console.error("Failed to upload file: ", error);
  }
};
// export type ProjectsLoaded = GetProjectsResponse;
// export const projectsLoaded = createAction<ProjectsLoaded>(PROJECTS_LOADED);
